/**
 * Search-and-pick filter for a record with too many rows to put in a <select>
 * — vendors today, and anything else that grows past a dropdown tomorrow.
 *
 * Built as a proper combobox rather than an input with a floating div:
 * role="combobox" + aria-expanded + aria-activedescendant + a real listbox, so
 * arrow keys, Enter, Escape and a screen reader all behave. The first pass was
 * a bare input whose results panel a document-level mousedown listener kept
 * closing on the same click that opened it.
 *
 * Once a record is chosen it shows that record's NAME. The id an admin would
 * otherwise see in the URL tells them nothing about who they filtered to.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Search, X } from 'lucide-react';
import { use_debounced_value } from '@/hooks/use-debounced-value';
import { FilterSlot } from './filter-bar';
import { cn } from '@/lib/utils';

export type EntityOption = { id: string; title: string; subtitle?: string | null };

export function EntityFilter({
  label,
  value,
  on_change,
  search,
  load_selected,
  placeholder = 'Search by name…',
  min_chars = 2,
  test_id = 'entity-filter',
  width = '14rem',
}: {
  label: string;
  value: string;
  on_change: (id: string) => void;
  /** Runs on a debounced term; returns the options to show. */
  search: (term: string) => Promise<EntityOption[]>;
  /** Resolves the currently selected id to something human. */
  load_selected: (id: string) => Promise<EntityOption>;
  placeholder?: string;
  min_chars?: number;
  test_id?: string;
  width?: string;
}) {
  const id = useId();
  const list_id = `${id}-list`;
  const [term, set_term] = useState('');
  const [open, set_open] = useState(false);
  const [active, set_active] = useState(0);
  const wrapper = useRef<HTMLDivElement>(null);
  const debounced = use_debounced_value(term, 250);
  const enabled = open && debounced.trim().length >= min_chars;

  const selected = useQuery({
    queryKey: [test_id, 'selected', value],
    queryFn: () => load_selected(value),
    enabled: value !== '',
    staleTime: 5 * 60_000,
  });

  const results = useQuery({
    queryKey: [test_id, 'search', debounced],
    queryFn: () => search(debounced.trim()),
    enabled,
    staleTime: 30_000,
  });

  const options = results.data ?? [];

  // Reset the highlight whenever the option list changes underneath it —
  // otherwise index 4 of the old results highlights an unrelated new row.
  useEffect(() => {
    set_active(0);
  }, [debounced]);

  // The listener goes on the whole control, not the results panel: the panel
  // does not exist until two characters are typed, so a ref on it is null
  // beforehand and every click "outside" included the input itself.
  useEffect(() => {
    if (!open) return;
    const on_pointer_down = (e: MouseEvent) => {
      if (!wrapper.current?.contains(e.target as Node)) set_open(false);
    };
    document.addEventListener('mousedown', on_pointer_down);
    return () => document.removeEventListener('mousedown', on_pointer_down);
  }, [open]);

  function commit(option: EntityOption) {
    on_change(option.id);
    set_open(false);
    set_term('');
  }

  function on_key_down(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      set_open(false);
      return;
    }
    if (!enabled || options.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      set_active((i) => (i + 1) % options.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      set_active((i) => (i - 1 + options.length) % options.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const choice = options[active];
      if (choice) commit(choice);
    }
  }

  if (value) {
    return (
      <FilterSlot label={label}>
        <div
          className="console-control flex items-center justify-between gap-1 pl-3 pr-1"
          style={{ width }}
        >
          <span className="min-w-0 truncate text-data font-medium" data-testid={`${test_id}-active`}>
            {selected.isLoading ? 'Loading…' : (selected.data?.title ?? value)}
          </span>
          <button
            type="button"
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => on_change('')}
            aria-label={`Clear ${label} filter`}
            data-testid={`${test_id}-clear`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </FilterSlot>
    );
  }

  return (
    <div ref={wrapper} className="relative">
      <FilterSlot label={label} html_for={id}>
        <div className="relative">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          />
          <input
            id={id}
            role="combobox"
            aria-expanded={enabled}
            aria-controls={list_id}
            aria-autocomplete="list"
            aria-activedescendant={enabled && options[active] ? `${id}-opt-${active}` : undefined}
            className="console-field pl-8"
            style={{ width }}
            placeholder={placeholder}
            value={term}
            onFocus={() => set_open(true)}
            onChange={(e) => {
              set_term(e.target.value);
              set_open(true);
            }}
            onKeyDown={on_key_down}
            data-testid={`${test_id}-input`}
          />
          {results.isFetching && enabled && (
            <Loader2 className="absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-muted-foreground" />
          )}
        </div>

        {enabled && (
          <ul
            id={list_id}
            role="listbox"
            aria-label={label}
            className="thin-scroll absolute top-full z-dropdown mt-1 max-h-64 w-72 overflow-y-auto rounded-panel bg-popover p-1 shadow-overlay animate-slide-up-fade"
            data-testid={`${test_id}-results`}
          >
            {results.isLoading ? (
              <li className="px-2 py-2 text-data text-muted-foreground">Searching…</li>
            ) : options.length === 0 ? (
              <li className="px-2 py-2 text-data text-muted-foreground">
                Nothing matches “{debounced.trim()}”.
              </li>
            ) : (
              options.map((o, i) => (
                <li key={o.id} id={`${id}-opt-${i}`} role="option" aria-selected={i === active}>
                  <button
                    type="button"
                    className={cn(
                      'flex w-full flex-col items-start gap-0.5 rounded-control px-2 py-1.5 text-left transition-colors',
                      i === active ? 'bg-primary/10' : 'hover:bg-muted',
                    )}
                    // mousedown, not click: the outside-pointer handler above
                    // fires first and would tear the list down mid-click.
                    onMouseDown={() => commit(o)}
                    onMouseEnter={() => set_active(i)}
                    data-testid={`${test_id}-option-${o.id}`}
                  >
                    <span className="text-data font-semibold">{o.title}</span>
                    {o.subtitle && (
                      <span className="text-micro text-muted-foreground">{o.subtitle}</span>
                    )}
                  </button>
                </li>
              ))
            )}
          </ul>
        )}
      </FilterSlot>
    </div>
  );
}
